'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import BlogFilters from '@/components/BlogFilters';
import blogPostsData from './data/blog-posts.json';

// Define the BlogPost type based on usage
interface BlogPost {
  slug: string;
  title: string;
  date: string | null;
  author: string;
  imageUrl: string;
  excerpt: string;
  tags: string[];
}

// Note: generateStaticParams removed since this is now a client component
// Individual blog post pages will handle their own static generation

export default function BlogPage() {
  const searchParams = useSearchParams();
  const query = searchParams.get('query') || '';
  const year = searchParams.get('year') || '';

  // Get available years for filter
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    blogPostsData.forEach((post) => {
      if (post.date) {
        years.add(new Date(post.date).getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a); // Sort descending
  }, []);

  // Filter and sort blog posts
  const filteredAndSortedPosts = useMemo(() => {
    let filtered = blogPostsData as BlogPost[];

    // Filter by search query
    if (query) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(lowerQuery) ||
          post.excerpt.toLowerCase().includes(lowerQuery) ||
          post.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
      );
    }

    // Filter by year
    if (year) {
      const selectedYear = parseInt(year);
      filtered = filtered.filter((post) => {
        if (!post.date) return false;
        return new Date(post.date).getFullYear() === selectedYear;
      });
    }

    // Sort by date (newest first)
    return filtered.sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [query, year]);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-4 text-transparent bg-clip-text bg-gradient-to-r from-[#59b9c1] to-[#59b9c1]">
          Blog
        </h1>
        <p className="text-center text-gray-400 mb-12 text-lg">
          Latest insights and updates from Data Jukebox
        </p>

        {/* Search and Filter Controls */}
        <BlogFilters availableYears={availableYears} />

        {filteredAndSortedPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredAndSortedPosts.map((post) => (
              <Link
                href={`/blog/${post.slug}`}
                key={post.slug}
                className="group block bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors duration-200"
              >
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={`/images/${post.imageUrl}`}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                </div>
                <div className="p-6">
                  {post.date && (
                    <div className="text-sm text-gray-400 mb-2">
                      {new Date(post.date).toLocaleDateString()}
                    </div>
                  )}
                  <h2 className="text-xl font-semibold text-white mb-3 group-hover:text-[#59b9c1] transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-gray-300 text-sm line-clamp-3">
                    {post.excerpt.replace(/&#x27;/g, "'").replace(/&quot;/g, '"').replace(/&amp;/g, '&')}
                  </p>
                  {post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {post.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-[#59b9c1] bg-opacity-20 text-[#59b9c1] text-xs rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-400 mt-10">
            <p className="text-lg mb-4">No blog posts found.</p>
            {(query || year) && (
              <Link
                href="/blog"
                className="text-[#59b9c1] hover:text-[#4aa7ae] underline"
              >
                Clear filters and show all posts
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
